import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowLeft, MapPin, Trash2, Edit3,
  Gauge, Timer, Thermometer, Eye, Wind, Shirt, Weight,
  Cloud, BatteryFull, BatteryLow, Star, Users, Activity,
  Fish, ExternalLink, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { useDiveLog, useDeleteDiveLog } from '@/data/dive-logs';
import { MapView } from '@/components/map/MapView';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { formatGermanDate, weekday, relativeFromNow } from '@/lib/format';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { cn } from '@/lib/utils';

const VISIBILITY_LABEL: Record<string, string> = { good: 'Gut', medium: 'Mittel', bad: 'Schlecht' };
const VISIBILITY_LEVEL: Record<string, number> = { good: 3, medium: 2, bad: 1 };
const CURRENT_LABEL:    Record<string, string> = { none: 'Keine', little: 'Wenig', medium: 'Mittel', strong: 'Stark' };
const CURRENT_LEVEL:    Record<string, number> = { none: 0, little: 1, medium: 2, strong: 3 };
const WETSUIT_LABEL:    Record<string, string> = { none: 'Keiner', shorty: 'Shorty', 'full-3mm': 'Full 3 mm', 'full-5mm': 'Full 5 mm', 'full-7mm': 'Full 7 mm', drysuit: 'Trockenanzug' };
const TYPE_LABEL:       Record<string, string> = { shore: 'Shore-Dive', boat: 'Boots-Dive', misc: 'Sonstige' };

export function DiveLogDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const { data: log, isLoading } = useDiveLog(Number(id));
  const del = useDeleteDiveLog();

  useDocumentTitle(log ? `${log.title}${log.userIndex ? ` (#${log.userIndex})` : ''}` : 'Tauchgang');

  if (isLoading || !log) {
    return <div className="grid h-full place-items-center p-8"><Spinner className="h-8 w-8" /></div>;
  }

  const handleDelete = async () => {
    if (!confirm('Tauchgang wirklich löschen?')) return;
    try {
      await del.mutateAsync(log.id);
      toast.success('Gelöscht.');
      void navigate({ to: '/logs' });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
    }
  };

  const hasCoords = log.diveSite?.geoLatitude != null && log.diveSite?.geoLongitude != null;
  const mapPoint  = hasCoords ? [{
    id:    log.diveSite!.id,
    lat:   log.diveSite!.geoLatitude!,
    lng:   log.diveSite!.geoLongitude!,
    title: log.diveSite!.title,
  }] : [];

  const buddies = (log.buddies ?? '')
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const animals = log.diveSite?.animals ?? [];

  const pressureConsumed = log.initialPressure != null && log.finalPressure != null
    ? Math.max(0, log.initialPressure - log.finalPressure)
    : null;
  const consumptionPerMin = pressureConsumed != null && log.diveTimeMinutes
    ? Math.round((pressureConsumed / log.diveTimeMinutes) * 10) / 10
    : null;

  return (
    <div className="space-y-6 p-4 sm:p-6 md:p-8">
      <header className="flex items-center justify-between">
        <Link to="/logs" className="inline-flex items-center gap-1 text-sm text-[var(--muted-foreground)] hover:text-ocean-700">
          <ArrowLeft className="h-4 w-4" /> Zurück zur Liste
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled>
            <Edit3 className="h-4 w-4" /> Bearbeiten
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDelete} disabled={del.isPending}>
            <Trash2 className="h-4 w-4" /> Löschen
          </Button>
        </div>
      </header>

      {/* Hero – Map + Title + 3 big stats */}
      <section className="relative overflow-hidden rounded-2xl shadow-lg">
        {hasCoords ? (
          <MapView points={mapPoint} height={340} singleZoom={9} rounded={false} />
        ) : (
          <div className="h-[340px] bg-gradient-to-br from-ocean-700 to-ocean-900" />
        )}
        {/* Two-stop overlay: even darkening across the whole map, then a stronger
            bottom-to-top gradient where the big numbers + title sit. */}
        <div className="pointer-events-none absolute inset-0 bg-ocean-950/45" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ocean-950/95 via-ocean-950/55 to-ocean-950/0" />

        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-ocean-950/55 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-white ring-1 ring-white/15 backdrop-blur-sm">
              <Calendar className="h-3 w-3" />
              {weekday(log.diveDate)}, {formatGermanDate(log.diveDate)}
            </span>
            <span className="rounded-full bg-ocean-950/55 px-2.5 py-1 text-[11px] font-medium text-white ring-1 ring-white/15 backdrop-blur-sm">
              {relativeFromNow(log.diveDate)}
            </span>
            {log.userIndex && (
              <span className="rounded-full bg-ocean-950/55 px-2.5 py-1 text-[11px] font-medium text-white ring-1 ring-white/15 backdrop-blur-sm">
                Tauchgang #{log.userIndex}
              </span>
            )}
          </div>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)] sm:text-3xl md:text-4xl">
            {log.title}
          </h1>
          {log.diveSite && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Link
                to="/sites/$id"
                params={{ id: String(log.diveSite.id) }}
                className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-ocean-950/55 px-2.5 py-1 text-xs text-white ring-1 ring-white/15 backdrop-blur-sm hover:bg-ocean-950/75"
              >
                <MapPin className="h-3 w-3" />
                <span className="font-medium">{log.diveSite.label}</span>
              </Link>
              {log.diveSite.siteType && (
                <span className="rounded-full bg-coral-500 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-sm">
                  {log.diveSite.siteType.replace('_', ' ')}
                </span>
              )}
            </div>
          )}

          {/* Big numbers strip */}
          <div className="pointer-events-auto mt-4 grid max-w-2xl grid-cols-3 gap-2 rounded-xl bg-ocean-950/55 p-3 ring-1 ring-white/15 backdrop-blur-md sm:gap-4">
            <HeroStat icon={Gauge} label="Tiefe"  value={log.maxDepth ? `${log.maxDepth} m` : '–'} />
            <HeroStat icon={Timer} label="Dauer"  value={log.diveTimeMinutes ? `${log.diveTimeMinutes} min` : '–'} />
            <HeroStat icon={Star}  label="Rating" value={log.rating ? '★'.repeat(log.rating) : '–'} />
          </div>
        </div>
      </section>

      {/* Conditions + Equipment row */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Conditions */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Eye className="h-4 w-4 text-ocean-700" /> Bedingungen
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <ConditionTile
              icon={Thermometer}
              label="Wassertemperatur"
              value={log.waterTemperature != null ? `${log.waterTemperature}°C` : '–'}
              tone={tempTone(log.waterTemperature)}
            />
            <ConditionTile
              icon={Eye}
              label="Sicht"
              value={log.visibility ? VISIBILITY_LABEL[log.visibility] ?? log.visibility : '–'}
              level={log.visibility ? VISIBILITY_LEVEL[log.visibility] ?? 0 : 0}
              levelMax={3}
            />
            <ConditionTile
              icon={Wind}
              label="Strömung"
              value={log.current ? CURRENT_LABEL[log.current] ?? log.current : '–'}
              level={log.current ? CURRENT_LEVEL[log.current] ?? 0 : 0}
              levelMax={3}
            />
            <ConditionTile
              icon={Activity}
              label="Typ"
              value={log.type ? TYPE_LABEL[log.type] ?? log.type : '–'}
            />
          </CardContent>
        </Card>

        {/* Equipment */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shirt className="h-4 w-4 text-ocean-700" /> Ausrüstung
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <KeyValRow icon={Shirt}  label="Anzug" value={log.wetSuitType ? WETSUIT_LABEL[log.wetSuitType] ?? log.wetSuitType : '–'} />
            <KeyValRow icon={Weight} label="Blei"  value={log.usedWeight != null ? `${log.usedWeight} kg` : '–'} />
            <KeyValRow
              icon={Cloud}
              label="Atemgas"
              value={log.airType === 'nitrox' ? `Nitrox ${log.nitroxO2Percentage ?? '?'} %` : log.airType === 'normal' ? 'Pressluft' : '–'}
            />
          </CardContent>
        </Card>
      </div>

      {/* Pressure & Consumption */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-ocean-700" /> Luftverbrauch
          </CardTitle>
        </CardHeader>
        <CardContent>
          {log.initialPressure == null && log.finalPressure == null ? (
            <p className="text-sm text-[var(--muted-foreground)]">Keine Druck-Daten erfasst.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <PressureTile icon={BatteryFull} label="Start"    value={log.initialPressure}  tone="green" />
                <PressureTile icon={BatteryLow}  label="Ende"     value={log.finalPressure}    tone="amber" />
                <PressureTile icon={Activity}    label="Verbraucht" value={pressureConsumed} suffix="bar" tone="ocean" />
              </div>

              {log.initialPressure != null && log.finalPressure != null && (
                <PressureBar initial={log.initialPressure} final={log.finalPressure} />
              )}

              {consumptionPerMin != null && (
                <p className="text-xs text-[var(--muted-foreground)]">
                  Verbrauch: <span className="font-medium text-[var(--foreground)]">{consumptionPerMin} bar/min</span>
                  {log.diveTimeMinutes ? ` über ${log.diveTimeMinutes} min Tauchzeit` : ''}.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Buddies & sightings */}
      {(buddies.length > 0 || animals.length > 0) && (
        <div className="grid gap-4 md:grid-cols-2">
          {buddies.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4 text-ocean-700" /> Buddies
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {buddies.map((b, i) => (
                    <span key={i} className="rounded-full bg-ocean-100 px-3 py-1 text-sm text-ocean-800 dark:bg-ocean-800 dark:text-ocean-100">
                      {b}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {animals.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Fish className="h-4 w-4 text-ocean-700" /> An dieser Site oft gesichtet
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {animals.map((a, i) => (
                    <span key={i} className="rounded-full bg-sand-100 px-3 py-1 text-sm text-ocean-900 dark:bg-sand-200/20 dark:text-sand-100">
                      {a}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Site card */}
      {log.diveSite && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4 text-ocean-700" /> Über diesen Tauchplatz
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {log.diveSite.regionPathStr && (
                <span className="text-[var(--muted-foreground)]">{log.diveSite.regionPathStr}</span>
              )}
              {log.diveSite.source !== 'user' && (
                <span className="rounded-full bg-ocean-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ocean-700 dark:bg-ocean-900/40 dark:text-ocean-200">
                  Quelle: {log.diveSite.source.replace('_', ' ')}
                </span>
              )}
              {log.diveSite.difficulty && (
                <span className="rounded-full bg-coral-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-coral-600">
                  {log.diveSite.difficulty}
                </span>
              )}
            </div>
            {log.diveSite.description && (
              <p className="text-sm leading-relaxed text-[var(--muted-foreground)]">{log.diveSite.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 pt-2 text-xs">
              <Link
                to="/sites/$id"
                params={{ id: String(log.diveSite.id) }}
                className="inline-flex items-center gap-1 font-medium text-ocean-700 hover:underline"
              >
                Site-Details öffnen <ExternalLink className="h-3 w-3" />
              </Link>
              {hasCoords && (
                <a
                  href={`https://www.google.com/maps?q=${log.diveSite.geoLatitude},${log.diveSite.geoLongitude}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 font-medium text-ocean-700 hover:underline"
                >
                  In Google Maps öffnen <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      {log.description && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notizen</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-line text-sm leading-relaxed">{log.description}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function HeroStat({ icon: Icon, label, value }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="text-center">
      <Icon className="mx-auto h-4 w-4 text-ocean-100" />
      <p className="mt-1 text-2xl font-semibold leading-tight text-white sm:text-3xl">{value}</p>
      <p className="text-[10px] font-medium uppercase tracking-wide text-ocean-200">{label}</p>
    </div>
  );
}

function ConditionTile({ icon: Icon, label, value, level, levelMax, tone }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  level?: number;
  levelMax?: number;
  tone?: 'cold' | 'mild' | 'warm';
}) {
  const toneBar = tone === 'cold' ? 'bg-ocean-600' : tone === 'warm' ? 'bg-coral-500' : tone === 'mild' ? 'bg-ocean-400' : 'bg-ocean-300';
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-[var(--muted-foreground)]">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[10px] font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-base font-semibold">{value}</p>
      {level !== undefined && levelMax !== undefined && (
        <div className="flex gap-0.5">
          {Array.from({ length: levelMax }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-1.5 flex-1 rounded-full',
                i < level ? 'bg-ocean-700' : 'bg-[var(--muted)]',
              )}
            />
          ))}
        </div>
      )}
      {tone && (
        <div className="h-1.5 w-full rounded-full bg-[var(--muted)] overflow-hidden">
          <div className={cn('h-full rounded-full', toneBar)} style={{ width: tone === 'cold' ? '30%' : tone === 'mild' ? '60%' : '90%' }} />
        </div>
      )}
    </div>
  );
}

function KeyValRow({ icon: Icon, label, value }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[var(--border)] pb-2 last:border-0 last:pb-0">
      <span className="flex items-center gap-2 text-sm text-[var(--muted-foreground)]">
        <Icon className="h-3.5 w-3.5" /> {label}
      </span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function PressureTile({ icon: Icon, label, value, suffix = 'bar', tone }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | null;
  suffix?: string;
  tone: 'green' | 'amber' | 'ocean';
}) {
  const colors = {
    green: 'bg-emerald-50  text-emerald-800 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-800',
    amber: 'bg-amber-50    text-amber-800   ring-amber-200   dark:bg-amber-900/30   dark:text-amber-200   dark:ring-amber-800',
    ocean: 'bg-ocean-50    text-ocean-800   ring-ocean-200   dark:bg-ocean-900/40   dark:text-ocean-100   dark:ring-ocean-800',
  }[tone];
  return (
    <div className={cn('rounded-xl p-3 ring-1', colors)}>
      <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="mt-1 text-xl font-semibold sm:text-2xl">
        {value != null ? value : '–'}
        {value != null && <span className="ml-1 text-xs font-normal opacity-70">{suffix}</span>}
      </p>
    </div>
  );
}

function PressureBar({ initial, final }: { initial: number; final: number }) {
  // Visualise the consumption from "initial bar" down to "final bar" on a 0..300 axis.
  const MAX = 300;
  const initialPct = Math.min(100, (initial / MAX) * 100);
  const finalPct   = Math.min(100, (final   / MAX) * 100);
  return (
    <div className="space-y-1.5">
      <div className="relative h-3 w-full rounded-full bg-[var(--muted)]">
        <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-coral-500 via-amber-400 to-emerald-500" style={{ width: `${initialPct}%` }} />
        <div className="absolute inset-y-0 left-0 rounded-l-full bg-gradient-to-r from-coral-500/80 to-coral-500/0" style={{ width: `${finalPct}%` }} />
        <div className="absolute -top-1 h-5 w-0.5 rounded bg-ocean-900" style={{ left: `calc(${finalPct}% - 1px)` }} />
      </div>
      <div className="flex justify-between text-[10px] text-[var(--muted-foreground)]">
        <span>0 bar</span>
        <span>{final} bar Ende</span>
        <span>{initial} bar Start</span>
        <span>{MAX} bar</span>
      </div>
    </div>
  );
}

function tempTone(temp: number | null | undefined): 'cold' | 'mild' | 'warm' | undefined {
  if (temp == null) return undefined;
  if (temp < 15) return 'cold';
  if (temp < 24) return 'mild';
  return 'warm';
}
